// SPDX-License-Identifier: AGPL-3.0-or-later

import {APP_PROTOCOL_PREFIX} from '@app/features/ui/utils/AppProtocol';

export interface KonvoMeetingAttendee {
	user_id: string;
	name: string;
	email: string;
}

export interface KonvoMeetingSuggestion {
	start: string;
	end: string;
	confidence: number;
}

export interface KonvoMeetingTimesResponse {
	attendees: Array<KonvoMeetingAttendee>;
	duration_minutes: number;
	suggestions: Array<KonvoMeetingSuggestion>;
	empty_reason: string | null;
}

export interface KonvoCalendarCreatePrefill {
	subject: string;
	start: string;
	end: string;
	attendees: Array<string>;
}

// Accepts 30m, 30min, 30 mins, 45 minutes, 1h, 1 hr, 1.5 hours, 1h30, 1h 30m and a bare 30.
const FREE_DURATION_PATTERN =
	/^(?:(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)\s*)?(?:(\d+)\s*(?:m|min|mins|minute|minutes)?)?$/i;
const FREE_CLOCK_DURATION_PATTERN = /^(\d+):([0-5]\d)$/;
const USER_MENTION_PATTERN = /<@!?(\d+)>/g;
const MIN_DURATION_MINUTES = 15;
const MAX_DURATION_MINUTES = 8 * 60;
const DEFAULT_DURATION_MINUTES = 30;
/** A duration can be spelled across a few tokens ("1 hr 30 min"), so try the trailing ones. */
const MAX_DURATION_TOKENS = 4;

function clampDuration(minutes: number): number | null {
	const rounded = Math.round(minutes);
	return rounded >= MIN_DURATION_MINUTES && rounded <= MAX_DURATION_MINUTES ? rounded : null;
}

export function parseFreeDuration(value: string): number | null {
	const trimmed = value.trim();
	const clock = FREE_CLOCK_DURATION_PATTERN.exec(trimmed);
	if (clock) return clampDuration(Number(clock[1]) * 60 + Number(clock[2]));
	const match = FREE_DURATION_PATTERN.exec(trimmed);
	if (!match || (!match[1] && !match[2])) return null;
	return clampDuration(Number(match[1] ?? 0) * 60 + Number(match[2] ?? 0));
}

function parseAttendees(attendeeText: string): Array<string> | null {
	const userIds = Array.from(attendeeText.matchAll(USER_MENTION_PATTERN), (match) => match[1]);
	const unmatched = attendeeText.replace(USER_MENTION_PATTERN, '').trim();
	const uniqueUserIds = Array.from(new Set(userIds));
	if (unmatched.length > 0 || uniqueUserIds.length < 1) return null;
	return uniqueUserIds;
}

export function parseFreeCommandArguments(value: string): {userIds: Array<string>; durationMinutes: number} | null {
	const tokens = value.trim().split(/\s+/).filter(Boolean);
	if (tokens.length === 0) return null;
	// Longest first, so "1 hr 30 min" wins over the trailing "30 min".
	for (let count = Math.min(MAX_DURATION_TOKENS, tokens.length); count > 0; count--) {
		const durationMinutes = parseFreeDuration(tokens.slice(-count).join(' '));
		if (durationMinutes == null) continue;
		const userIds = parseAttendees(tokens.slice(0, -count).join(' '));
		if (userIds) return {userIds, durationMinutes};
	}
	// No duration given at all — everything is mentions, so use the default.
	const userIds = parseAttendees(tokens.join(' '));
	return userIds ? {userIds, durationMinutes: DEFAULT_DURATION_MINUTES} : null;
}

export function excludeMeetingOrganizer(userIds: ReadonlyArray<string>, organizerId: string | null): Array<string> {
	return organizerId == null ? [...userIds] : userIds.filter((userId) => userId !== organizerId);
}

export function buildCalendarCreateUrl(prefill: KonvoCalendarCreatePrefill): string {
	const query = new URLSearchParams({
		create: '1',
		subject: prefill.subject,
		start: prefill.start,
		end: prefill.end,
		attendees: prefill.attendees.join(','),
	});
	// Markdown masked links only accept absolute URLs, and a bare `)` inside the query would
	// terminate the link early, so use the app protocol and keep parentheses percent-encoded.
	const encoded = query.toString().replaceAll('(', '%28').replaceAll(')', '%29');
	return `${APP_PROTOCOL_PREFIX}calendar?${encoded}`;
}

export function parseCalendarCreatePrefill(search: URLSearchParams): KonvoCalendarCreatePrefill | null {
	if (search.get('create') !== '1') return null;
	const start = search.get('start') ?? '';
	const end = search.get('end') ?? '';
	const startMs = Date.parse(start);
	const endMs = Date.parse(end);
	if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
	const attendees = (search.get('attendees') ?? '')
		.split(',')
		.map((email) => email.trim())
		.filter((email) => email.includes('@'))
		.slice(0, 50);
	return {
		subject: (search.get('subject') ?? '').slice(0, 200),
		start: new Date(startMs).toISOString(),
		end: new Date(endMs).toISOString(),
		attendees,
	};
}

function formatSlot(startValue: string, endValue: string): string {
	const start = new Date(startValue);
	const end = new Date(endValue);
	const day = new Intl.DateTimeFormat(undefined, {weekday: 'short', day: 'numeric', month: 'short'}).format(start);
	const time = new Intl.DateTimeFormat(undefined, {hour: '2-digit', minute: '2-digit'});
	return `${day}, ${time.format(start)}\u2013${time.format(end)}`;
}

function formatNameList(names: ReadonlyArray<string>): string {
	return new Intl.ListFormat(undefined, {style: 'long', type: 'conjunction'}).format(names);
}

export function formatMeetingDuration(minutes: number): string {
	const hours = Math.floor(minutes / 60);
	const remainder = minutes % 60;
	const parts: Array<string> = [];
	if (hours > 0) parts.push(`${hours} hr`);
	if (remainder > 0) parts.push(`${remainder} min`);
	return parts.join(' ') || `${minutes} min`;
}

export function formatMeetingTimesMessage(response: KonvoMeetingTimesResponse): string {
	const names = response.attendees.map((attendee) => attendee.name);
	const attendeeNames = formatNameList(names);
	if (response.suggestions.length === 0) {
		let reason: string;
		switch (response.empty_reason) {
			case 'organizerUnavailable':
				reason = 'Outlook reports that your calendar has no matching gap during your configured work hours.';
				break;
			case 'attendeesUnavailable':
				reason = `Outlook found no matching work-hour slot where all of these attendees are available: ${attendeeNames}.`;
				break;
			case 'attendeesUnavailableOrUnknown':
				reason = `Outlook could not confirm enough availability for ${attendeeNames}. This can happen when a calendar is external, unavailable, or marked busy.`;
				break;
			case 'locationsUnavailable':
				reason = 'Outlook could not find an available meeting location.';
				break;
			default:
				reason = 'Outlook did not return a more specific reason.';
		}
		return `I couldn't find a ${response.duration_minutes}-minute mutual gap for ${attendeeNames} in the next 14 days. ${reason}\n\n/free checks each person's primary Outlook calendar during their configured work hours. Shared calendars shown in Konvo\u2014such as an HR calendar\u2014are display-only and are ignored.`;
	}
	const subject = `Meeting with ${attendeeNames}`;
	const emails = response.attendees.map((attendee) => attendee.email);
	const lines = response.suggestions.map((suggestion, index) => {
		const url = buildCalendarCreateUrl({subject, start: suggestion.start, end: suggestion.end, attendees: emails});
		return `${index + 1}. ${formatSlot(suggestion.start, suggestion.end)} \u2014 [Create event](${url})`;
	});
	const heading = `**Next times everyone is free** \u00b7 ${formatMeetingDuration(response.duration_minutes)} with ${attendeeNames}`;
	const footer = '-# Times are shown in your timezone. Pick one to open a prefilled event.';
	return `${heading}\n\n${lines.join('\n')}\n\n${footer}`;
}
